import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames/bind";
import { Button, Input, Modal } from "antd";
import { DownloadOutlined, SendOutlined } from "@ant-design/icons";
import { Chatbot } from "../../../../assets/svg";
import {
  WordIcon,
  PdfIcon,
  PptxIcon,
  ExcelIcon,
  DefaultIcon,
} from "../../../../assets";
import { useRecruitment } from "../../../../context/RecruitmentContext";
import {
  buildChatMessagesFromUploadPayload,
  isCertificateImageAttachment,
  normalizeApiChatMessages,
  resolveRecruitmentImagePreviewUrl,
  resolveRecruitmentDownloadFullUrl,
} from "../../../../utils/constants/uploadCvUtils";
import CampaignJdMessage from "./CampaignJdMessage";
import ChatMarkdownContent from "./ChatMarkdownContent";
import UploadCvModal from "../Modal/UploadCvModal";
import styles from "./index.module.sass";
import { hasCandidateInfo, markRecruitmentSessionActive } from "../../../../utils/helps";
import { GENERAL_CHAT_KEY } from "../../../../utils/constants/config";
import { actionEnsureRecruitmentChatSession, actionSendRecruitmentChatMessage } from "../action";
import { buildCandidatePayload, buildJobContextPayload, buildRecruitmentSessionKey, buildUserInfoPayload, getMessageFileAttachments, mergeChatMessages, mergeSessionMessagesWithCampaignUi } from "../../../../utils/constants/chatMessageUtils";
const cx = classNames.bind(styles);

const SCROLL_BOTTOM_THRESHOLD = 80;

const normalizeFilePayloads = (rawFiles) => {
  if (!Array.isArray(rawFiles)) return [];

  return rawFiles
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          name: `cv-${index + 1}`,
          url: item,
        };
      }

      if (!item || typeof item !== "object") return null;

      const path =
        // item.url ||
        item.cv_path ||
        item.path ||
        // item.file_url ||
        // item.link ||
        "";

      return {
        name:
          item.name ||
          // item.file_name ||
          // item.filename ||
          item.original_name ||
          // item.cv_file ||
          `cv-${index + 1}`,
        url: path,
        cv_path: item.cv_path || path,
        path,
        file_path: path,
        extension:
          item.extension ||
          item.extension_file ||
          (path || item.name || "").split(".").pop()?.toLowerCase(),
        type: item.type || item.mime_type || "",
      };
    })
    .filter((file) => file && (file.url || file.cv_path));
};

const ChatImageFileAttachment = ({ imageUrl, filename, onDownload }) => {
  const [previewVisible, setPreviewVisible] = useState(false);

  const openPreview = () => setPreviewVisible(true);

  const handlePreviewKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPreview();
    }
  };

  return (
    <div className={cx("chatFileAttachment")}>
      <button
        type="button"
        className={cx(
          "chatFileAttachmentIcon",
          "chatFileAttachmentThumb",
          "chatFileAttachmentPreviewTrigger",
        )}
        onClick={openPreview}
        onKeyDown={handlePreviewKeyDown}
        title="Xem ảnh"
        aria-label={`Xem ảnh ${filename}`}
      >
        <img
          src={imageUrl}
          alt=""
          className={cx("chatFileThumbImg")}
          loading="lazy"
        />
      </button>
      <span
        className={cx("chatFileAttachmentName", "chatFileAttachmentNameClickable")}
        onClick={openPreview}
        onKeyDown={handlePreviewKeyDown}
        role="button"
        tabIndex={0}
        title="Xem ảnh"
      >
        {filename}
      </span>
      <button
        type="button"
        className={cx("chatFileAttachmentDownload")}
        onClick={onDownload}
        aria-label={`Tải xuống ${filename}`}
        title="Tải xuống"
      >
        <DownloadOutlined />
      </button>
      <Modal
        open={previewVisible}
        footer={null}
        centered
        width="auto"
        title={filename}
        onCancel={() => setPreviewVisible(false)}
        destroyOnHidden
        wrapClassName={cx("chatImagePreviewModalWrap")}
        className={cx("chatImagePreviewModal")}
      >
        <img
          src={imageUrl}
          alt={filename}
          className={cx("chatImagePreviewFull")}
        />
      </Modal>
    </div>
  );
};

const CampaignChatPanel = ({
  campaign,
  onApply,
  showApplyButton,
  showQuickActions = true,
  isMobile = false,
}) => {
  const {
    candidateInfo,
    setCandidateInfo,
    getMessagesForCampaign,
    setMessagesForCampaign,
  } = useRecruitment();

  const campaignId = campaign?.id;
  const conversationKey = campaignId ?? GENERAL_CHAT_KEY;
  const messages = getMessagesForCampaign(conversationKey);
  const setMessages = (updater) => setMessagesForCampaign(conversationKey, updater);
  const [messageInput, setMessageInput] = useState("");
  const [jdPinned, setJdPinned] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [sending, setSending] = useState(false);
  const [candidateId, setCandidateId] = useState(null);
  const [stickyApplyOpen, setStickyApplyOpen] = useState(false);
  const scrollRef = useRef(null);
  const jdRef = useRef(null);
  // const [viewFile, setViewFile] = useState(false);

  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  const isNearBottomRef = useRef(true);
  const pendingScrollToEndRef = useRef(false);

  const focusMessageInput = () => {
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const scrollToLatest = (behavior = "smooth") => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior });
    }
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
    isNearBottomRef.current = true;
    setShowScrollToBottom(false);
  };

  const updateScrollPosition = () => {
    const el = scrollRef.current;

    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    const nearBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD;

    isNearBottomRef.current = nearBottom;

    setShowScrollToBottom(distanceFromBottom > SCROLL_BOTTOM_THRESHOLD);
  };

  const hasUserMessages = messages.some((message) => message.from === "user");
  const userInfoPayload = buildUserInfoPayload(candidateInfo);
  const jobContextPayload = buildJobContextPayload(campaign, campaignId);
  const sessionKey = buildRecruitmentSessionKey(
    userInfoPayload.phone,
    campaignId,
  );
  const cvUploadContext = {
    userId: userInfoPayload.phone,
    jobContext: jobContextPayload,
    session: sessionKey,
    userInfo: userInfoPayload,
    candidate: buildCandidatePayload(candidateInfo),
  };

  const handleDownloadFile = (attachment, e) => {
    e?.stopPropagation();
    const downloadUrl = resolveRecruitmentDownloadFullUrl(attachment);
    if (!downloadUrl) return;
    window.open(downloadUrl, "_blank");
  };

  // const handleViewFile = (attachment, filename = "file") => {
  //   const record = buildAttachmentViewRecord(attachment, filename);
  //   console.log("recordddddd", record);
  //   const filePath = record?.cv_path || record?.path;
  //   if (!filePath) return;

  //   const extension = record.extension;

  //   switch (extension) {
  //     case "pdf":
  //     case "jpeg":
  //     case "png":
  //     case "jpg":
  //     case "heic":
  //     case "heif":
  //     case "gif":
  //     case "webp":
  //       window.open(getFullUrlStaticFilePDF(filePath), "_blank");
  //       break;
  //     case "doc":
  //     case "docx":
  //     case "pptx":
  //     case "pptm":
  //     case "ppt":
  //     case "xls":
  //     case "xlsx":
  //       setViewFile(record);
  //       break;
  //     default:
  //       window.open(getFullUrlStaticFilePDF(filePath), "_blank");
  //       break;
  //   }
  // };

  useEffect(() => {
    if (!hasCandidateInfo(candidateInfo)) {
      setSessionReady(false);
      setChatSessionId(null);
      return undefined;
    }
    let cancelled = false;

    const initSession = async () => {
      setSessionReady(false);

      try {
        const sessionPayload = {
          candidate: buildCandidatePayload(candidateInfo),
        };

        if (campaignId) {
          sessionPayload.campaign_id = campaignId;
        }

        const response = await actionEnsureRecruitmentChatSession(sessionPayload);
        if (cancelled) return;
        const payload = response?.data;
        if (payload?.success && payload?.data) {
          const {
            session,
            messages: apiMessages,
            candidate,
            certificates: sessionCertificates = [],
          } = payload.data;
          setCandidateId(candidate?.id ?? null);
          if (session?.id) {
            setChatSessionId(session.id);
            markRecruitmentSessionActive(conversationKey);
          }
          setMessagesForCampaign(
            conversationKey,
            mergeSessionMessagesWithCampaignUi(
              campaign,
              candidateInfo.fullName,
              normalizeApiChatMessages(apiMessages, sessionCertificates),
            ),
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) {
          setSessionReady(true);
        }
      }
    };

    initSession();

    return () => {
      cancelled = true;
    };
  }, [
    campaignId,
    candidateInfo.email,
    candidateInfo.fullName,
    candidateInfo.phone,
    conversationKey,
    setMessagesForCampaign,
  ]);

  useEffect(() => {
    if (sessionReady && chatSessionId) {
      focusMessageInput();
    }
  }, [sessionReady, chatSessionId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollPosition, { passive: true });
    updateScrollPosition();

    return () => el.removeEventListener("scroll", updateScrollPosition);
  }, [campaignId]);

  useEffect(() => {
    updateScrollPosition();
  }, [messages]);

  useLayoutEffect(() => {
    if (!pendingScrollToEndRef.current && !sending) return;
    scrollToLatest("smooth");
    if (!sending) {
      pendingScrollToEndRef.current = false;
    }
  }, [messages, sending]);

  useEffect(() => {
    if (messages.length === 0 || !hasUserMessages) return;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.type === "jd") return;
    if (pendingScrollToEndRef.current) return;
    if (!isNearBottomRef.current) return;
    scrollToLatest("smooth");
  }, [messages, hasUserMessages]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = jdRef.current;
    if (!root || !target || !hasUserMessages) {
      setJdPinned(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setJdPinned(!entry.isIntersecting);
      },
      { root, threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [campaignId, messages.length, hasUserMessages]);

  const scrollToJd = () => {
    jdRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCvUploaded = (uploadResult) => {
    const files = uploadResult?.files ?? uploadResult;
    const normalized = normalizeFilePayloads(
      Array.isArray(files) ? files : [],
    );

    if (normalized.length > 0) {
      setCandidateInfo({ ...candidateInfo, cv_files: normalized });
    }

    const uploadMessages = buildChatMessagesFromUploadPayload(
      uploadResult?.payload,
    );
    if (uploadMessages.length > 0) {
      pendingScrollToEndRef.current = true;
      setMessages((prev) => {
        return mergeChatMessages(prev, uploadMessages);
      });
    }
  };

  const handleSendMessage = async (textOverride) => {
    const text = (
      typeof textOverride === "string" ? textOverride : messageInput
    ).trim();
    if (!text || !chatSessionId || sending) return;
    const tempUserId = `temp-user-${Date.now()}`;
    pendingScrollToEndRef.current = true;
    setMessages((prev) => [...prev, { id: tempUserId, from: "user", text }]);
    setMessageInput("");
    setSending(true);

    try {
      const messagePayload = {
        session_id: sessionKey,
        message: text,
        user_info: userInfoPayload,
      };

      if (jobContextPayload) {
        messagePayload.job_context = jobContextPayload;
      }

      const response = await actionSendRecruitmentChatMessage(messagePayload);

      const payload = response?.data;

      if (payload?.success && payload?.data?.messages) {
        const persistedMessages = payload.data.messages;

        setMessages((prev) => {
          const withoutTemp = prev.filter((item) => item.id !== tempUserId);

          return mergeChatMessages(withoutTemp, persistedMessages);
        });

        if (payload.data.session?.id) {
          setChatSessionId(payload.data.session.id);
          markRecruitmentSessionActive(conversationKey);
        }
      } else {
        setMessages((prev) => prev.filter((item) => item.id !== tempUserId));
      }
    } catch (error) {
      console.log(error);
      setMessages((prev) => prev.filter((item) => item.id !== tempUserId));
    } finally {
      pendingScrollToEndRef.current = true;
      setSending(false);
      focusMessageInput();
    }
  };

  const handleScrollToBottom = () => {
    isNearBottomRef.current = true;
    scrollToLatest("smooth");
  };

  const chatInputDisabled = !sessionReady || !chatSessionId;
  const sendDisabled = chatInputDisabled || sending;

  return (
    <div className={cx("chatPanel", { chatPanelMobile: isMobile })}>
      {campaign && jdPinned && (
        <div className={cx("jdStickyPin")}>
          <button
            type="button"
            className={cx("jdStickyPinScroll")}
            onClick={scrollToJd}
          >
            <span className={cx("jdStickyPinAvatar")} aria-hidden="true">
              <Chatbot />
            </span>

            <span className={cx("jdStickyPinText")}>
              <strong>{campaign.title}</strong>

              <em>{campaign.department}</em>
            </span>
          </button>

          {showApplyButton && (
            <button
              type="button"
              className={cx("applyButton", "jdStickyPinApply")}
              onClick={() => setStickyApplyOpen(true)}
            >
              <span className={cx("applyIcon")}>📄</span>
              Ứng tuyển ngay
            </button>
          )}
        </div>
      )}

      <div className={cx("chatMessagesWrap")}>
        <div ref={scrollRef} className={cx("chatMessages")}>
          {messages.map((message, messageIndex) => {
            if (campaign && message.type === "jd") {
              return (
                <CampaignJdMessage
                  key={message.id ?? `jd-${messageIndex}`}
                  ref={jdRef}
                  campaign={campaign}
                  campaignId={campaignId}
                  candidateId={candidateId}
                  uploadContext={cvUploadContext}
                  onCvUploaded={handleCvUploaded}
                  onApply={onApply}
                  showApplyButton={showApplyButton}
                />
              );
            }

            if (message.type === "file") {
              const fileAttachments = getMessageFileAttachments(message);

              return (
                <div
                  key={message.id ?? `file-${messageIndex}`}
                  className={cx("messageRow", {
                    user: message.from === "user",
                    bot: message.from === "bot",
                  })}
                >
                  {message.from === "bot" && (
                    <span className={cx("botMessageAvatar")} aria-hidden="true">
                      <Chatbot />
                    </span>
                  )}

                  <div className={cx("messageBubble", message.from === "user" ? "userMessageBubble" : "")}>
                    {fileAttachments.map((attachment, fileIndex) => {
                      const filename =
                        attachment?.original_name || attachment?.name || "file";
                      const ext = attachment?.extension || "";
                      const imageUrl = isCertificateImageAttachment(attachment)
                        ? resolveRecruitmentImagePreviewUrl(attachment)
                        : null;

                      if (imageUrl) {
                        return (
                          <ChatImageFileAttachment
                            key={`${message.id ?? messageIndex}-file-${fileIndex}`}
                            imageUrl={imageUrl}
                            filename={filename}
                            onDownload={(e) => handleDownloadFile(attachment, e)}
                          />
                        );
                      }

                      let Icon = DefaultIcon;
                      if (ext === "pdf") Icon = PdfIcon;
                      else if (ext === "doc" || ext === "docx") Icon = WordIcon;
                      else if (ext === "ppt" || ext === "pptx") Icon = PptxIcon;
                      else if (ext === "xls" || ext === "xlsx" || ext === "csv") {
                        Icon = ExcelIcon;
                      }

                      return (
                        <div
                          key={`${message.id ?? messageIndex}-file-${fileIndex}`}
                          className={cx("chatFileAttachment")}
                        >
                          <span className={cx("chatFileAttachmentIcon")}>
                            <Icon aria-hidden="true" />
                          </span>
                          <span className={cx("chatFileAttachmentName")}>{filename}</span>
                          <button
                            type="button"
                            className={cx("chatFileAttachmentDownload")}
                            onClick={(e) => handleDownloadFile(attachment, e)}
                            aria-label={`Tải xuống ${filename}`}
                            title="Tải xuống"
                          >
                            <DownloadOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={message.id ?? `msg-${messageIndex}`}
                className={cx("messageRow", {
                  user: message.from === "user",

                  bot: message.from === "bot",
                })}
              >
                {message.from === "bot" && (
                  <span className={cx("botMessageAvatar")} aria-hidden="true">
                    <Chatbot />
                  </span>
                )}

                {message.from === "bot" ? (
                  <div className={cx("messageBubble")}>
                    <ChatMarkdownContent content={message.text} />
                  </div>
                ) : (
                  <div className={cx("messageBubble", "userMessageBubble")}>
                    <span>{message.text}</span>
                  </div>
                )}
              </div>
            );
          })}

          {sending && (
            <div className={cx("messageRow", "bot", "typingRow")}>
              <span className={cx("botMessageAvatar")} aria-hidden="true">
                <Chatbot />
              </span>
              <div className={cx("messageBubble", "typingBubble")}>
                <span className={cx("typingText")}>Đang xử lý yêu cầu</span>
                <span className={cx("typingDots")} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          )}

          <div
            ref={messagesEndRef}
            className={cx("chatMessagesAnchor")}
            aria-hidden="true"
          />
        </div>

        {showScrollToBottom && (
          <Button
            type="primary"
            shape="circle"
            className={cx("scrollToBottomBtn")}
            onClick={handleScrollToBottom}
            aria-label="Cuộn đến tin nhắn mới nhất"
            title="Tin nhắn mới nhất"
          >
            <span className={cx("scrollToBottomIcon")} aria-hidden="true" />
          </Button>
        )}
      </div>

      {showQuickActions && (
        <div className={cx("quickActions")}>
          <Button
            size="small"
            className={cx("quickActionBtn")}
            disabled={sendDisabled}
            onClick={() =>
              handleSendMessage(
                campaign
                  ? "Quy trình ứng tuyển như thế nào?"
                  : "Quy trình tuyển dụng chung của công ty như thế nào?",
              )
            }
          >
            {campaign ? "Quy trình ứng tuyển" : "Quy trình tuyển dụng"}
          </Button>

          <Button
            size="small"
            className={cx("quickActionBtn")}
            disabled={sendDisabled}
            onClick={() =>
              handleSendMessage(
                campaign
                  ? "Yêu cầu công việc chi tiết ra sao?"
                  : "Công ty đang tuyển những vị trí nào?",
              )
            }
          >
            {campaign ? "Yêu cầu công việc" : "Vị trí đang tuyển"}
          </Button>

          <Button
            size="small"
            className={cx("quickActionBtn")}
            disabled={sendDisabled}
            onClick={() =>
              handleSendMessage(
                campaign
                  ? "Tôi muốn nộp CV."
                  : "Lĩnh vực làm việc của công ty",
              )
            }
          >
            {campaign ? "Tôi muốn nộp CV" : "Lĩnh vực làm việc của công ty"}
          </Button>

          <Button
            size="small"
            className={cx("quickActionBtn")}
            disabled={sendDisabled}
            onClick={() =>
              handleSendMessage(
                campaign
                  ? "Phúc lợi của vị trí này có những gì?"
                  : "Công ty có những phúc lợi gì?",
              )
            }
          >
            Phúc lợi
          </Button>

          <Button
            size="small"
            className={cx("quickActionBtn")}
            disabled={sendDisabled}
            onClick={() =>
              handleSendMessage("Công ty có những hoạt động ngoại khóa nào?")
            }
          >
            Hoạt động ngoại khóa
          </Button>
        </div>
      )}

      <div className={cx("chatInput")}>
        <Input.TextArea
          style={{ fontSize: "14px !important" }}
          ref={messageInputRef}
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          autoFocus
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder={
            sessionReady
              ? "Tin nhắn"
              : "Đang kết nối phiên trò chuyện..."
          }
          disabled={chatInputDisabled}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || sendDisabled || e.shiftKey) return;
            e.preventDefault();
            handleSendMessage();
          }}
        />

        <Button
          type="primary"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSendMessage}
          loading={sending}
          disabled={sendDisabled}
          icon={<SendOutlined />}
        >
          Gửi
        </Button>
      </div>

      <UploadCvModal
        open={stickyApplyOpen}
        onClose={() => setStickyApplyOpen(false)}
        campaignId={campaignId}
        candidateId={candidateId}
        uploadContext={cvUploadContext}
        onSuccess={(uploadResult) => {
          handleCvUploaded(uploadResult);
          onApply?.();
        }}
      />
      {/* {viewFile && <ViewFile record={viewFile} onCancel={() => setViewFile(false)} />} */}
    </div>
  );
};

export default CampaignChatPanel;
